import { useMemo, useState } from 'react';
import { useNominableClientContacts } from '../../hooks/useNominableClientContacts';
import { cn } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent } from '../ui/popover';
import { SelectSearchInput, SelectTrigger } from '../ui/select-primitives';

interface ClientContactMultiSelectorProps {
	/** The client whose contacts can be chosen. */
	organizationId: string;
	value: string[];
	onChange: (v: string[]) => void;
	skip?: boolean;
}

/**
 * Picks a quote's client contacts inside a form, before the quote exists.
 * Nobody chosen means everyone at the client is emailed, so that's the
 * placeholder rather than "Select…".
 */
export function ClientContactMultiSelector({
	organizationId,
	value,
	onChange,
	skip = false,
}: ClientContactMultiSelectorProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const { contacts, loading } = useNominableClientContacts(organizationId, skip);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return contacts;
		return contacts.filter((c) => `${c.name} ${c.email ?? ''}`.toLowerCase().includes(q));
	}, [contacts, query]);

	const toggle = (id: string) =>
		onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

	const displayLabel =
		value.length > 0
			? value
					.map((id) => contacts.find((c) => c.id === id)?.name)
					.filter(Boolean)
					.join(', ') || null
			: null;

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setQuery('');
			}}
		>
			<SelectTrigger
				label={displayLabel}
				placeholder="Everyone at the client"
				hasValue={value.length > 0}
				variant="form"
				count={value.length}
			/>
			<PopoverContent align="start" className="w-(--anchor-width) gap-0 p-0">
				<SelectSearchInput value={query} onChange={setQuery} />
				<div className="scroll-thin max-h-[200px] overflow-y-auto p-1">
					{value.length > 0 && (
						<button
							onClick={() => onChange([])}
							className="mb-0.5 flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-left text-2xs text-text-4 transition-colors hover:text-text-2"
						>
							Clear all (email everyone)
						</button>
					)}
					{loading && contacts.length === 0 && (
						<div className="py-4 text-center text-xs text-text-4">Loading…</div>
					)}
					{!loading && filtered.length === 0 && (
						<div className="px-2 py-3 text-center text-sm text-text-4">
							{query ? 'No results' : 'This client has no contacts yet'}
						</div>
					)}
					{filtered.map((contact) => {
						const selected = value.includes(contact.id);
						return (
							<button
								key={contact.id}
								onClick={() => toggle(contact.id)}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
									selected
										? 'bg-surface-fill text-text-1'
										: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
								)}
							>
								<Checkbox
									checked={selected}
									tabIndex={-1}
									className="pointer-events-none"
								/>
								<span className="min-w-0 flex-1 truncate">{contact.name}</span>
								{contact.pending && (
									<span className="shrink-0 text-2xs text-text-4">Invited</span>
								)}
							</button>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
}
